import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sparkles, ArrowRight, ImagePlus, RotateCcw, AlertCircle, Lock, Download, CheckCircle2, Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useCreateListing } from '@/contexts/CreateListingContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { PhotoComparisonSlider } from './PhotoComparisonSlider';

type WidgetState = 'idle' | 'processing' | 'result' | 'unlocking' | 'unlocked' | 'error';

const DEMO_COOLDOWN_KEY = 'demo_enhance_usage';
// Soft, client-side first line only — the real cap is enforced server-side
// (durable per-IP + global daily limits). This just avoids a wasted round-trip.
const MAX_DEMOS = 3;
// Set before redirecting an anonymous user to /auth so we can finish the unlock
// automatically when they return signed-in.
const PENDING_UNLOCK_KEY = 'listbright_pending_demo_unlock';

function checkClientCooldown(): { allowed: boolean; remaining: number } {
  try {
    const raw = localStorage.getItem(DEMO_COOLDOWN_KEY);
    if (!raw) return { allowed: true, remaining: MAX_DEMOS };
    const count = JSON.parse(raw);
    return { allowed: count < MAX_DEMOS, remaining: Math.max(0, MAX_DEMOS - count) };
  } catch { return { allowed: true, remaining: MAX_DEMOS }; }
}

function recordClientUsage() {
  try {
    const raw = localStorage.getItem(DEMO_COOLDOWN_KEY);
    const count = raw ? JSON.parse(raw) : 0;
    localStorage.setItem(DEMO_COOLDOWN_KEY, JSON.stringify(count + 1));
  } catch {}
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/** Downscale + JPEG-compress so the base64 payload comfortably fits server limits. */
async function compressImage(file: File, maxDim = 1920, quality = 0.85): Promise<string> {
  const dataUrl = await fileToBase64(file);
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = reject;
    el.src = dataUrl;
  });
  const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return dataUrl;
  ctx.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL('image/jpeg', quality);
}

export function TryItNowWidget({ onGetStarted, inline, sampleOriginalSrc, sampleEnhancedSrc }: { onGetStarted: () => void; inline?: boolean; sampleOriginalSrc?: string; sampleEnhancedSrc?: string }) {
  const [state, setState] = useState<WidgetState>('idle');
  const [originalUrl, setOriginalUrl] = useState<string | null>(null);
  const [watermarkedUrl, setWatermarkedUrl] = useState<string | null>(null);
  const [cleanUrl, setCleanUrl] = useState<string | null>(null);
  const [demoId, setDemoId] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const autoUnlockHandled = useRef(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setPendingDemoPhoto } = useCreateListing();

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return;

    setState('processing');
    setErrorMsg('');

    const cooldown = checkClientCooldown();
    if (!cooldown.allowed) {
      setErrorMsg(`You've used all ${MAX_DEMOS} free demos. Create a free account for unlimited enhancements!`);
      setState('error');
      return;
    }

    const localUrl = URL.createObjectURL(file);
    setOriginalUrl(localUrl);

    try {
      const base64 = await compressImage(file);

      const { data, error } = await supabase.functions.invoke('demo-enhance-photo', {
        body: { imageBase64: base64 },
      });

      if (error) throw new Error(error.message || 'Enhancement failed');
      if (data?.error) throw new Error(data.error);
      if (!data?.watermarkedImageUrl) throw new Error('No enhanced image returned');

      recordClientUsage();
      setWatermarkedUrl(data.watermarkedImageUrl);
      setDemoId(data.demoId ?? null);
      setState('result');
    } catch (err: any) {
      console.error('Demo enhancement failed:', err);
      setErrorMsg(err?.message || 'Something went wrong. Please try again.');
      setState('error');
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const dt = new DataTransfer();
      dt.items.add(file);
      if (fileInputRef.current) {
        fileInputRef.current.files = dt.files;
        fileInputRef.current.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }, []);

  const handleReset = () => {
    if (originalUrl) URL.revokeObjectURL(originalUrl);
    setOriginalUrl(null);
    setWatermarkedUrl(null);
    setCleanUrl(null);
    setDemoId(null);
    setErrorMsg('');
    setState('idle');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Exchange a demoId for the clean, un-watermarked image (signed-in users only).
  const runUnlock = useCallback(async (id: string) => {
    setState('unlocking');
    setErrorMsg('');
    try {
      const { data, error } = await supabase.functions.invoke('unlock-demo-photo', {
        body: { demoId: id },
      });
      if (error) throw new Error(error.message || 'Could not unlock the photo');
      if (data?.error) throw new Error(data.error);
      if (!data?.cleanImageUrl) throw new Error('No image returned');
      setCleanUrl(data.cleanImageUrl);
      setState('unlocked');
    } catch (err: any) {
      console.error('Demo unlock failed:', err);
      setErrorMsg(err?.message || 'Could not unlock the photo. Please try again.');
      setState('error');
    }
  }, []);

  const handleUnlock = () => {
    if (!demoId) return;
    if (user) {
      runUnlock(demoId);
    } else {
      // Stash the demo so we can finish the unlock when they come back signed in.
      try {
        sessionStorage.setItem(PENDING_UNLOCK_KEY, JSON.stringify({ demoId }));
      } catch {}
      toast({
        title: 'Create your free account to unlock',
        description: "We'll instantly hand you the clean, watermark-free photo once you're in.",
      });
      navigate('/auth');
    }
  };

  // Final "explore the full suite" CTA. Carries the just-unlocked photo into
  // the create-listing flow so the user's listing starts with it pre-loaded
  // (and pre-enhanced — it won't cost them another photo edit).
  const handleCta = () => {
    if (cleanUrl) {
      setPendingDemoPhoto({ originalUrl: originalUrl ?? undefined, enhancedUrl: cleanUrl });
    }
    if (user) {
      onGetStarted();
    } else {
      navigate('/auth');
    }
  };

  // When a freshly-signed-in user lands back on the page, finish any pending
  // unlock automatically and bring the result into view.
  useEffect(() => {
    if (!user || autoUnlockHandled.current) return;
    let pending: { demoId?: string } | null = null;
    try {
      const raw = sessionStorage.getItem(PENDING_UNLOCK_KEY);
      pending = raw ? JSON.parse(raw) : null;
    } catch { pending = null; }
    if (!pending?.demoId) return;

    autoUnlockHandled.current = true;
    sessionStorage.removeItem(PENDING_UNLOCK_KEY);
    setDemoId(pending.demoId);
    toast({ title: 'Welcome to ListBright!', description: 'Unlocking your enhanced photo…' });
    setTimeout(() => containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
    runUnlock(pending.demoId);
  }, [user, runUnlock]);

  const interactiveZone = (
    <div ref={containerRef} className="scroll-mt-24">
          {state === 'idle' && (
            <div className="rounded-2xl overflow-hidden border border-border/50 bg-card shadow-elevated">
              {/* Sample comparison slider — shown as an example of the result */}
              {sampleOriginalSrc && sampleEnhancedSrc ? (
                <div className="relative">
                  <PhotoComparisonSlider originalSrc={sampleOriginalSrc} enhancedSrc={sampleEnhancedSrc} />
                  <div className="absolute top-3 left-3 bg-foreground/70 text-background text-[11px] font-semibold px-2.5 py-1 rounded-full shadow backdrop-blur">
                    Example
                  </div>
                </div>
              ) : null}

              {/* Explicit upload dropzone — the unmistakable way to try your own photo */}
              <div
                role="button"
                tabIndex={0}
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInputRef.current?.click(); } }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
                className="cursor-pointer p-6 sm:p-8 text-center border-t border-dashed border-border/70 hover:bg-muted/40 focus:bg-muted/40 focus:outline-none transition-colors"
                aria-label="Upload a property photo to enhance"
              >
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                  <ImagePlus className="w-6 h-6 text-primary" />
                </div>
                <p className="font-display font-semibold text-foreground">Upload your photo to try it</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Drag &amp; drop or click to choose — JPG or PNG, no account needed.
                </p>
                <Button
                  variant="hero"
                  size="lg"
                  className="mt-4 shadow-lg"
                  onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                  style={{ animation: 'glow-pulse 2.5s ease-in-out infinite' }}
                >
                  <ImagePlus className="w-4 h-4 mr-2" />
                  Upload your photo
                  <ArrowRight className="w-4 h-4 ml-2" style={{ animation: 'nudge 1.8s ease-in-out infinite' }} />
                </Button>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          )}

          {state === 'processing' && (
            <div className="rounded-2xl overflow-hidden border border-border/50 bg-card shadow-elevated">
              {/* Show user's photo dimmed while processing */}
              <div className="relative aspect-[4/3] bg-muted">
                {originalUrl && (
                  <img src={originalUrl} alt="Your photo" className="absolute inset-0 w-full h-full object-cover opacity-50" />
                )}
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                  <div className="w-14 h-14 rounded-full bg-background/90 flex items-center justify-center animate-pulse shadow-lg">
                    <Sparkles className="w-7 h-7 text-primary" />
                  </div>
                  <div className="bg-background/90 backdrop-blur-sm rounded-xl px-5 py-3 text-center shadow">
                    <p className="font-medium text-foreground text-sm">AI is enhancing your photo...</p>
                    <p className="text-xs text-muted-foreground mt-0.5">This usually takes 10–20 seconds</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {state === 'error' && (
            <div className="rounded-2xl border border-destructive/30 bg-card p-12 sm:p-16 text-center">
              <div className="flex flex-col items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-destructive/10 flex items-center justify-center">
                  <AlertCircle className="w-7 h-7 text-destructive" />
                </div>
                <p className="font-medium text-foreground">{errorMsg}</p>
                <div className="flex flex-col sm:flex-row gap-2">
                  {demoId && user && (
                    <Button variant="hero" onClick={() => runUnlock(demoId)}>
                      <Lock className="w-4 h-4 mr-2" />
                      Try unlocking again
                    </Button>
                  )}
                  <Button variant="outline" onClick={handleReset}>
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Try another photo
                  </Button>
                </div>
              </div>
            </div>
          )}

          {state === 'unlocking' && (
            <div className="rounded-2xl border border-border/50 bg-card p-12 sm:p-16 text-center">
              <div className="flex flex-col items-center gap-4">
                <Loader2 className="w-10 h-10 text-primary animate-spin" />
                <p className="font-medium text-foreground">Unlocking your clean, full-resolution photo…</p>
              </div>
            </div>
          )}

          {state === 'result' && originalUrl && watermarkedUrl && (
            <div className="space-y-5">
              <p className="text-center text-lg font-display font-bold text-foreground">Incredible, right?</p>

              {/* Before/after comparison slider — the enhanced side carries a
                  baked-in watermark, so it stays a preview until unlocked. */}
              <div className="relative rounded-2xl overflow-hidden shadow-elevated border border-border/50">
                <PhotoComparisonSlider originalSrc={originalUrl} enhancedSrc={watermarkedUrl} />
                <div className="absolute top-3 left-3 bg-emerald-500/90 text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" /> AI Enhanced + Lightly Staged
                </div>
                <div className="absolute top-3 right-3 bg-foreground/70 text-background text-xs font-semibold px-3 py-1.5 rounded-full shadow flex items-center gap-1.5 backdrop-blur">
                  <Lock className="w-3.5 h-3.5" /> Watermarked preview
                </div>
              </div>

              {/* Register-to-unlock card */}
              <div className="rounded-2xl border border-primary/30 bg-primary/5 p-5 text-center">
                <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                  <Lock className="w-5 h-5 text-primary" />
                </div>
                <p className="font-display font-bold text-foreground text-lg">Unlock the clean, full-res photo — free</p>
                <p className="text-sm text-muted-foreground mt-1 mb-4">
                  Create a free account to remove the watermark, download this photo, and unlock the full ListBright suite.
                </p>
                <div className="flex flex-col sm:flex-row gap-2 justify-center">
                  <Button variant="hero" size="lg" onClick={handleUnlock} className="shadow-lg w-full sm:w-auto" disabled={!demoId}>
                    <Sparkles className="w-4 h-4 mr-2" />
                    {user ? 'Unlock my photo' : 'Create free account to unlock'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  <Button variant="ghost" size="lg" onClick={handleReset} className="text-muted-foreground w-full sm:w-auto">
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Try another photo
                  </Button>
                </div>
              </div>
            </div>
          )}

          {state === 'unlocked' && cleanUrl && (
            <div className="space-y-5">
              <div className="text-center">
                <div className="inline-flex items-center gap-2 text-emerald-600 font-semibold">
                  <CheckCircle2 className="w-5 h-5" /> Unlocked — watermark removed
                </div>
              </div>

              <div className="relative rounded-2xl overflow-hidden shadow-elevated border border-border/50 bg-muted">
                <img src={cleanUrl} alt="Your clean enhanced photo" className="w-full h-auto" />
              </div>

              <div className="flex flex-col sm:flex-row gap-2 justify-center">
                <a href={cleanUrl} download="listbright-enhanced.jpg" className="w-full sm:w-auto">
                  <Button variant="hero" size="lg" className="shadow-lg w-full">
                    <Download className="w-4 h-4 mr-2" />
                    Download photo
                  </Button>
                </a>
                <Button variant="outline" size="lg" onClick={handleCta} className="w-full sm:w-auto">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Turn this photo into a full listing
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button variant="ghost" size="lg" onClick={handleReset} className="text-muted-foreground w-full sm:w-auto">
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Enhance another
                </Button>
              </div>
            </div>
          )}
    </div>
  );

  if (inline) return interactiveZone;

  return (
    <section className="py-20 lg:py-32 border-b border-border/40 bg-background">
      <div className="container max-w-[1280px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20 items-center">

          {/* Editorial column */}
          <div className="lg:col-span-4 flex flex-col">
            <span className="text-xs uppercase tracking-widest text-muted-foreground mb-6">
              Live Demonstration
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold leading-[1.1] text-foreground mb-5">
              See What AI Can Do With <span className="text-gradient">Your Photo</span>
            </h2>
            <p className="text-base leading-relaxed text-muted-foreground max-w-[36ch] mb-8">
              Upload any property photo. Our AI reads the architecture and relights every surface — instantly.
            </p>

            <div className="h-px w-12 bg-border mb-6" />

            <div className="space-y-1 mb-8">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">
                Trusted globally
              </p>
              <p className="text-2xl font-display font-bold text-foreground">
                14,892 photos enhanced
              </p>
            </div>

            <div>
              <Badge variant="secondary" className="bg-primary/10 text-primary border-0">
                <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                Try free — no signup
              </Badge>
            </div>
          </div>

          {/* Visual column */}
          <div className="lg:col-span-8">
            {interactiveZone}
          </div>
        </div>
      </div>
    </section>
  );
}
